import React, { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSessionStateStore } from '@renderer/stores/sessionState'
import { useConfigStore } from '@renderer/stores/configState'
import { useCountdown } from '@renderer/utils/countdownUtils'
import { useTranslation } from 'react-i18next'
import { FrameOption } from '@renderer/types/configState'
import NewspaperLayout from './components/NewspaperLayout'

const FramePage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { projectId } = useParams()
  const {
    frames: allFrames,
    frameCategories,
    config: globalConfig,
    getPageSettings
  } = useConfigStore()
  const config = globalConfig?.pages.frame
  const { setSessionState, logActivity } = useSessionStateStore()
  const [selectedCategory, setSelectedCategory] = useState('all')
  const isPotrait =
    (globalConfig?.screenSize.height || 1080) > (globalConfig?.screenSize.width || 1920)
  const [remainingTime, setRemainingTime] = useState<string>('--')
  const welcomeSettings = getPageSettings('welcome')
  const isCategoryModeEnabled = config?.settings?.categoryModeEnabled || false

  const [currentFrames, setCurrentFrames] = useState<FrameOption[]>([])
  const [selectedFrame, setSelectedFrame] = useState<string | null>(null)

  // Calculate frame aspect ratio for layout adjustments
  const selectedFrameData = allFrames.find((f) => f._id === selectedFrame)
  const frameAspectRatio = selectedFrameData
    ? selectedFrameData.drawParams.canvasWidth / selectedFrameData.drawParams.canvasHeight
    : 1
  const isTallFrame = frameAspectRatio < 0.5 // For frames like 2R (500x1500 = 0.33)

  // Initialize countdown with the useCountdown hook
  const frameTimeout = config?.settings?.frameTimeout || 0
  const countdown = useCountdown(frameTimeout)

  // Set up countdown callbacks
  useEffect(() => {
    if (frameTimeout > 0) {
      countdown.setOnTick(() => {
        setRemainingTime(countdown.getFormattedRemainingSeconds())
      })

      countdown.setOnComplete(() => {
        // Auto-proceed if frame is selected, otherwise select first frame
        if (selectedFrame) {
          handleNext()
        } else if (currentFrames.length > 0) {
          setSelectedFrame(currentFrames[0]._id)
          setTimeout(() => handleNext(), 100)
        }
      })

      countdown.start()
    }

    return (): void => {
      if (frameTimeout > 0) {
        countdown.stop()
      }
    }
  }, [frameTimeout, selectedFrame, currentFrames])

  useEffect(() => {
    let initialFramesToDisplay: FrameOption[] = []

    if (isCategoryModeEnabled && frameCategories.length > 0) {
      // Set initial selected category to the first one
      setSelectedCategory(frameCategories[0].name)
      initialFramesToDisplay = frameCategories[0].frames || []
    } else {
      // Fallback to 'all' if not in category mode or no categories
      setSelectedCategory('all')
      initialFramesToDisplay = allFrames
    }
    setCurrentFrames(initialFramesToDisplay)

    if (initialFramesToDisplay.length > 0) {
      setSelectedFrame(initialFramesToDisplay[0]._id)
    }
  }, [isCategoryModeEnabled, frameCategories, allFrames])

  useEffect(() => {
    if (isCategoryModeEnabled && selectedCategory !== 'all') {
      const category = frameCategories.find((cat) => cat.name === selectedCategory)
      setCurrentFrames(category?.frames || [])
    } else if (!isCategoryModeEnabled) {
      // In non-category mode or 'all' category selected, use all frames
      setCurrentFrames(allFrames)
    }
  }, [isCategoryModeEnabled, selectedCategory, frameCategories, allFrames])

  useEffect(() => {
    if (currentFrames.length > 0 && !selectedFrame) {
      setSelectedFrame(currentFrames[0]._id)
    } else if (currentFrames.length === 0 && selectedFrame) {
      setSelectedFrame(null)
    }
  }, [currentFrames, selectedFrame])

  const handleNext = (): void => {
    if (!selectedFrame) return

    const frame = allFrames.find((f) => f._id === selectedFrame)
    if (!frame) return

    // Find first photo type image and get its ratio
    const photoImage = frame.drawParams.images.find((img) => img.type.startsWith('photo-'))
    const photoRatio = photoImage ? photoImage.width / photoImage.height : null

    setSessionState({
      userPreferences: {
        frameId: selectedFrame,
        framePhotoRatio: photoRatio
      }
    })
    const frameName = allFrames.find((f) => f._id === selectedFrame)?.name
    logActivity(`Selected frame: ${frameName || selectedFrame}`, { frameId: selectedFrame })

    // Determine next page based on capture mode and startWithPayment setting
    const captureMode = globalConfig?.pages.capture.settings.mode || 'standard'
    let nextPage = welcomeSettings?.startWithPayment ? 'capture' : 'checkout'

    // For frame-based mode, always go to capture (skip checkout)
    if (captureMode === 'frame-based') {
      nextPage = 'capture'
    }

    navigate(`/${projectId}/${nextPage}`)
  }

  // Set up countdown callbacks with dependencies on selectedFrame
  useEffect(() => {
    if (frameTimeout > 0) {
      // Set the onTick callback to update the remaining time display
      countdown.setOnTick(() => {
        setRemainingTime(countdown.getFormattedRemainingSeconds())
      })

      // Set the onComplete callback that will use the latest state
      countdown.setOnComplete(() => {
        const frameName = allFrames.find((f) => f._id === selectedFrame)?.name
        logActivity(
          `Frame selection timed out. Automatically selected frame: ${frameName || selectedFrame}`,
          {
            frameId: selectedFrame,
            timeoutDuration: frameTimeout
          }
        )
        handleNext()
      })
    }
  }, [selectedFrame, frameTimeout, setSessionState])

  // Start the countdown on mount
  useEffect(() => {
    if (frameTimeout > 0) {
      countdown.start()

      return (): void => {
        countdown.stop()
      }
    }
    return
  }, [frameTimeout])

  return (
    <NewspaperLayout disableOverflow={true}>
      <div className="w-full h-full flex relative" data-page="frame">
      {/* Frame Selection */}
      <div className="flex-1 flex flex-col w-full max-w-[70%] relative z-50">
        {/* Category Tabs - Only show if frameCategories exist and are not empty */}
        {isCategoryModeEnabled && frameCategories && frameCategories.length > 0 && (
          <div className="shadow-xl w-full relative z-50">
            <div className="overflow-x-auto w-full flex justify-left gap-4 px-12 py-4 z-50">
              {frameCategories.map((category) => (
                <button
                  type="button"
                  key={category.name}
                  onClick={() => setSelectedCategory(category.name)}
                  className={`px-8 py-3 text-xl whitespace-nowrap transition-all font-serif rounded-none shadow-none
                    ${
                      selectedCategory === category.name
                        ? 'bg-[#1a1a1a] text-[#fafaf8] border-2 border-double border-[#2a2a2a]'
                        : 'border-2 border-double border-[#2a2a2a] text-newspaper-black bg-newspaper-white'
                    }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Frames Grid */}
        <div
          className={`flex-1 overflow-y-auto px-4 mt-14 ${isCategoryModeEnabled && frameCategories?.length ? 'py-12' : 'py-8'} relative z-50`}
        >
          <div
            className={`grid ${isPotrait ? 'grid-cols-1' : 'grid-cols-3'} gap-12 items-end`}
            role="listbox"
            title={t('frame.framesListbox', 'Frames')}
          >
            {currentFrames.map((frame) => (
              <button
                role="option"
                aria-label={frame.name}
                type="button"
                key={frame._id}
                onClick={() => setSelectedFrame(frame._id)}
                className={`cursor-pointer transition-all flex flex-col p-8 items-center
                  ${selectedFrame === frame._id ? 'scale-105' : ''}`}
              >
                <img
                  crossOrigin="anonymous"
                  src={frame.thumbnail}
                  alt={frame.name}
                  className={`w-full object-contain border-2 border-double border-[#2a2a2a] rounded-none ${
                    selectedFrame === frame._id
                      ? 'border-4 border-[#1a1a1a]'
                      : 'border-2 border-[#2a2a2a]'
                  }`}
                />
              </button>
            ))}
          </div>
        </div>
      </div>

        {/* Frame Preview */}
        <div
          className={`bg-newspaper-white border-2 border-double border-[#2a2a2a] flex flex-col items-center w-full h-full ${isPotrait ? 'p-12 max-w-[55%]' : 'p-8 max-w-[35%]'}`}
        >
        {/* Image Container - Scrollable for tall frames */}
        <div
          className={`flex-1 flex items-center justify-center w-full min-h-0 ${isTallFrame ? 'overflow-y-auto' : ''}`}
        >
          <div className={`${isTallFrame ? 'w-[300px]' : 'w-[426px]'} px-4 flex-shrink-0`}>
            <img
              src={selectedFrameData?.thumbnail || ''}
              alt={t('frame.previewAlt', 'Preview')}
              crossOrigin="anonymous"
              className="w-full object-contain drop-shadow-xl"
            />
          </div>
        </div>

          {/* Button Area - Always visible at bottom */}
          <div className="flex flex-col items-center gap-4 flex-shrink-0 py-16">
            {(config?.settings?.frameTimeout || 0) > 0 && (
              <span className="text-2xl text-newspaper-black font-serif font-bold">
                {remainingTime}
              </span>
            )}
            {/* Next Button */}
            <button
              type="button"
              onClick={handleNext}
              className="hover:opacity-90 cursor-pointer font-serif font-medium transition-all duration-500 select-none active:scale-95 focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed bg-[#1a1a1a] text-[#fafaf8] border-2 border-double border-[#2a2a2a] px-10 py-2 text-3xl rounded-none shadow-none"
            >
              {t('common.next', 'Next')}
            </button>
          </div>
        </div>
      </div>
    </NewspaperLayout>
  )
}

export default FramePage
